import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import api, { API, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rp, num, pct } from "@/lib/format";
import PeriodPicker, { periodQuery, todayWib } from "@/components/PeriodPicker";
import { PageTitle, Panel } from "@/components/common";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Payroll() {
  const { isEditor } = useAuth();
  const [pv, setPv] = useState({ period: "BULANAN", date: todayWib() });
  const month = pv.date.slice(0, 7);
  const [type, setType] = useState("ALL");
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    const p = new URLSearchParams(periodQuery(pv));
    if (type !== "ALL") p.append("worker_type", type);
    api.get(`/payroll?${p.toString()}`).then((r) => setRows(r.data.rows));
  }, [pv, type]);
  useEffect(() => { load(); }, [load]);

  const slip = (wid) => {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/export/payslip-pdf?month=${month}&worker_id=${wid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); }).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = `slip_${wid}_${month}.pdf`; a.click();
        URL.revokeObjectURL(url); toast.success(`Slip upah ${wid} diunduh`);
      }).catch(() => toast.error("Gagal membuat slip PDF"));
  };

  const totals = rows.reduce((a, r) => ({
    gross: a.gross + r.gross_sewing_pay, bon: a.bon + r.total_bon,
    net: a.net + r.net_payable, pay: a.pay + r.payment, out: a.out + r.outstanding_payable,
  }), { gross: 0, bon: 0, net: 0, pay: 0, out: 0 });

  return (
    <div>
      <PageTitle title="Payroll & Ongkos Jahit" desc="Net = Gross − Bon − Potongan Reject − Kasbon ± Koreksi. Angka bon/reject/kasbon mengikuti totalan yang tersimpan; 'Belum ditotal' = transaksi apa adanya."
        right={
          <div className="flex gap-2">
            <Tabs value={type} onValueChange={setType}>
              <TabsList>
                <TabsTrigger value="ALL">Semua</TabsTrigger>
                <TabsTrigger value="PENGEPUL">Pengepul</TabsTrigger>
                <TabsTrigger value="PENJAHIT_DALAM">Penjahit</TabsTrigger>
              </TabsList>
            </Tabs>
            <PeriodPicker value={pv} onChange={setPv} testId="payroll-period" allowCustom />
          </div>
        } />

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="table-payroll">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
                {["Worker", "Setor", "Good", "Reject", "Rate%", "Gross Ongkos", "Bon", "Pot. Reject", "Kasbon", "Pinjaman", "Net Payable", "Payment", "Outstanding", "Totalan", "Slip"].map((h) => <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.worker_id} className="border-b border-slate-100 hover:bg-slate-50/80">
                  <td className="px-3 py-2.5 whitespace-nowrap"><span className="font-bold text-slate-900">{r.worker_id}</span> <span className="text-slate-500">{r.worker_name}</span></td>
                  <td className="px-3 py-2.5 mono text-right">{num(r.total_setor)}</td>
                  <td className="px-3 py-2.5 mono text-right text-emerald-700">{num(r.total_good)}</td>
                  <td className="px-3 py-2.5 mono text-right text-rose-600">{num(r.total_reject)}</td>
                  <td className="px-3 py-2.5 mono text-right text-slate-500">{pct(r.reject_rate)}</td>
                  <td className="px-3 py-2.5 mono text-right">{rp(r.gross_sewing_pay)}</td>
                  <td className="px-3 py-2.5 mono text-right text-indigo-700">{rp(r.total_bon)}</td>
                  <td className="px-3 py-2.5 mono text-right text-rose-600">{rp(r.reject_deduction)}</td>
                  <td className="px-3 py-2.5 mono text-right text-orange-700">{rp(r.kasbon)}</td>
                  <td className="px-3 py-2.5 mono text-right text-fuchsia-700" data-testid={`payroll-pinjaman-${r.worker_id}`}>{rp(r.pinjaman || 0)}</td>
                  <td className="px-3 py-2.5 mono text-right font-semibold">{rp(r.net_payable)}</td>
                  <td className="px-3 py-2.5 mono text-right text-emerald-700">{rp(r.payment)}</td>
                  <td className="px-3 py-2.5 mono text-right font-bold text-rose-700">{rp(r.outstanding_payable)}</td>
                  <td className="px-3 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${r.totalan_status === "DITOTAL" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`} data-testid={`totalan-status-${r.worker_id}`}>{r.totalan_status === "DITOTAL" ? "Ditotal" : "Belum ditotal"}</span></td>
                  <td className="px-3 py-2.5"><button onClick={() => slip(r.worker_id)} className="text-slate-600 hover:text-slate-900" title="Slip upah PDF" data-testid={`payslip-${r.worker_id}`}><FileText className="w-4 h-4" /></button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={15} className="px-3 py-10 text-center text-slate-400">Tidak ada data payroll.</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200 font-bold mono">
                  <td className="px-3 py-3 text-xs uppercase tracking-wider text-slate-600">Total</td>
                  <td colSpan={4}></td>
                  <td className="px-3 py-3 text-right">{rp(totals.gross)}</td>
                  <td className="px-3 py-3 text-right text-indigo-700">{rp(totals.bon)}</td>
                  <td></td><td></td><td></td>
                  <td className="px-3 py-3 text-right">{rp(totals.net)}</td>
                  <td className="px-3 py-3 text-right text-emerald-700">{rp(totals.pay)}</td>
                  <td className="px-3 py-3 text-right text-rose-700">{rp(totals.out)}</td>
                  <td></td><td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
